import * as React from 'react';
import { Button, Form, Input, Row, message, InputNumber } from 'antd';
import I18N from 'src/services/I18NService';
import WalletService, {
  IAccountInfo,
  ICardInfo,
  IGetCashApplyParam,
  IGetCashConfirmParam,
  EAccountMemberType,
} from 'src/services/WalletService';
import '../views/Wallet/wallet.css';
import { FormComponentProps, FormItemProps } from 'antd/lib/form';
import { IVerificationCode } from 'src/entity/LoginParam';
import TencentCaptchaButton from 'src/components/common/TencentCaptchaButton';

interface IGetCashFormProps extends FormComponentProps {
  /** 提现银行卡信息 */
  cardInfo: ICardInfo;
  /** 开户账号信息 */
  account: IAccountInfo;
  /** 余额 */
  balance: number;
  /** 是否显示手续费提示 */
  showHandlingFeeTip: boolean;
  /** 表单提交成功后关闭模态窗函数 */
  closeModal(): void;
}

interface IGetCashFormState {
  /** 是否正在提交 */
  isLoading: boolean;
  /** 控制金额输入禁用 */
  amountDisabled: boolean;
}

/**
 * 创建提现Form组件
 */
class CreateGetCashForm extends React.Component<IGetCashFormProps, IGetCashFormState> {
  /** 订单号(验证码接口返回) */
  private orderNo = '';
  private walletService = WalletService;
  // 提现最小金额控制(非生产环境: 0.01元, 生产环境: 1元)
  readonly getCashLimit = process.env.REACT_APP_SECRET_CODE === 'production' ? 1 : 0.01;
  constructor(props: IGetCashFormProps) {
    super(props);
    this.state = { isLoading: false, amountDisabled: false, };
  }

  /**
   * 发送验证码
   * @param data
   */
  async verifyCode(data: IVerificationCode) {
    this.props.form.validateFields(['amount'], async (err) => {
      if (!err) {
        this.orderNo = '';
        const amount: number = this.props.form.getFieldValue('amount');
        if (data.mobile) {
          const { cardInfo, account } = this.props;
          const param: IGetCashApplyParam = {
            card_no: cardInfo.card_no,
            card_prop: account.member_type === EAccountMemberType.Individual ? 0 : 1,
            amount,
          };
          const accountId = account.id as string;
          const res = await this.walletService.applyGetCash(accountId, param);
          if (!!res && !!res.order_no) {
            this.orderNo = res.order_no;
            message.success(I18N.get('login_get_verify'));
            this.setState({ amountDisabled: true });
          }
        }
      }
    });
  }

  /**
   * 确认提现
   */
  getCashConfirm = (e: React.SyntheticEvent) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        if (!this.orderNo) {
          message.error('动态验证码不符(N5C0048)');
        } else {
          const param: IGetCashConfirmParam = {
            order_no: this.orderNo,
            verification_code: values.verification_code,
          };
          this.setState({ isLoading: true });
          const accountId = this.props.account.id as string;
          const res = await this.walletService.confirmGetCash(accountId, param);
          this.setState({ isLoading: false });
          if (!!res && !!res.order_no) {
            message.success('提现成功');
            this.props.closeModal();
          }
        }
      }
    });
  };

  render() {
    const { form, cardInfo, account, balance, showHandlingFeeTip } = this.props;
    const { isLoading, amountDisabled } = this.state;
    const { getFieldDecorator } = form;
    const maxCashTip = `最大提现金额${balance.toFixed(2)}`;
    const formItemLayout: FormItemProps = {
      labelCol: { span: 7 },
      wrapperCol: { span: 15 },
    };
    // 控制获取验证码按钮禁用
    const amount = this.props.form.getFieldValue('amount');
    const captchaDisabled = typeof amount === 'number' ? amount <= this.getCashLimit : true;
    // 最小提现金额控制
    const getCashLimit = this.getCashLimit;
    // 提现手机号统一使用phone字段
    const valiedatePhone = account.phone || '';
    return (
      <Form onSubmit={this.getCashConfirm}>
        <Form.Item label={'提现银行卡'} {...formItemLayout}>
          <div className="cashCard">
            <div className="cashCard-lt">
              <img src={cardInfo.logo} />
            </div>
            <div className="cashCard-rt">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* 测试要求 企业账户不应该显示法人名称 */}
                <span style={{ fontSize: 16 }}>{account.name}</span>
                <span style={{ marginLeft: 10, color: '#888' }}>{cardInfo.bank_name}</span>
                <span style={{ marginLeft: 10, color: '#888' }}>
                  {cardInfo.card_type === 1 && <span>储蓄卡</span>}
                  {cardInfo.card_type === 2 && <span>信用卡</span>}
                </span>
              </div>
              <div style={{ marginTop: 10 }}>{cardInfo.card_no}</div>
            </div>
          </div>
        </Form.Item>
        <Form.Item label={'金额'} {...formItemLayout}>
          {getFieldDecorator(`amount`, {
            rules: [
              {
                required: true,
                message: '请输入提现金额',
              },
              {
                validator(rule, value, callback) {
                  if (typeof value === 'number' && value <= getCashLimit) {
                    callback(`提现金额必须大于${getCashLimit}元`);
                  } else {
                    callback();
                  }
                },
              },
            ],
          })(<InputNumber style={{ width: '100%' }} disabled={amountDisabled} placeholder={maxCashTip} min={getCashLimit} max={balance} precision={2} />)}
        </Form.Item>
        <Form.Item label={I18N.get('common_mobile')} {...formItemLayout}>
          <span className="ant-form-text">{valiedatePhone || ''}</span>
        </Form.Item>
        <Form.Item label={I18N.get('login_verify_code')} style={{ marginBottom: 0 }} {...formItemLayout}>
          {getFieldDecorator('verification_code', {
            rules: [
              {
                required: true,
                message: `${I18N.get('common_input')}${I18N.get('login_verify_code')}`,
              },
              {
                pattern: /^[0-9]{6}$/gi,
                message: '验证码格式错误',
              },
            ],
          })(
            <Input
              placeholder={`${I18N.get('common_input')}${I18N.get('login_verify_code')}`}
              suffix={
                <TencentCaptchaButton
                  isDomestic={true}
                  phoneOrEmail={valiedatePhone || ''}
                  isDisabled={captchaDisabled}
                  onSend={(data) => this.verifyCode(data)}
                />
              }
            />
          )}
        </Form.Item>
        {
          showHandlingFeeTip && <div className="ant-form-extra" style={{ margin: '0 0 20px 60px', textAlign: 'center' }}>
            手续费：1 元/笔
          </div>
        }
        <Row gutter={24} style={{ margin: 0 }}>
          <Form.Item style={{ textAlign: 'center', margin: 0 }}>
            <Button type="primary" loading={isLoading} htmlType="submit">
              {I18N.get('common_ok')}
            </Button>
          </Form.Item>
        </Row>
      </Form>
    );
  }
}
export const GetCashForm = Form.create<IGetCashFormProps>()(CreateGetCashForm);


// WEBPACK FOOTER //
// ./src/components/common/CreateGetCashForm.tsx